import { useMemo, useState } from "preact/hooks";
import type { LinkResolver } from "../data/links";
import { signed } from "../engine";
import type { CheckChoices, CheckResult, CheckSpec, DamageResult, DiceSupply, DieSlot, RollRecord, RolledFormula } from "../rolls";
import { DEGREE_LABELS, MAX_RANGE_INCREMENTS, cryptoDice, diceOf, fixedDice, isRerollable, isValidFormula, newRollId, rangePenalty, resolveCheck, rollDamage, rollFormula, selectedSubstitution } from "../rolls";
import type { Session, SessionMaxima, TableAction } from "../session";
import { applyIWR, describeApplication } from "../session";
import type { ActorContext } from "./enrich";
import { enrichDescription, readEnrichedClick } from "./enrich";
import { Modal } from "./modal";
import type { RollPlan } from "./roll-spec";
import type { RollRequest } from "./rolls";

interface Props {
  plan: RollPlan;
  /** Start by asking for the physical dice (a long press). */
  physical: boolean;
  session: Session | null;
  /** The Session's maxima, for the immunities, weaknesses and resistances damage applied to self goes through (#22). */
  maxima?: SessionMaxima | null;
  actor: ActorContext | null;
  links: LinkResolver | null;
  busy: boolean;
  /** Record the Roll (and what it does to the Session) once it is final. */
  onCommit: (record: RollRecord, effects: TableAction[]) => void;
  /** Start another Roll from this one (the damage after a hit, the flat check after persistent damage). */
  onFollow: (request: RollRequest) => void;
  onClose: () => void;
  /** The dice source; the cryptographic one unless a test supplies its own. */
  supply?: DiceSupply;
}

/** A check in the making: the dice rolled so far and every choice, resolved anew on each change. */
interface CheckDraft {
  id: string;
  at: string;
  dice: number[];
  choices: CheckChoices;
  dc: number | null;
  physical: boolean;
  rerollOf: string | null;
  heroPoint: boolean;
  /** Whether what the Roll consumes (ammunition, ended Effects) was already committed with an earlier draft. */
  consumed: boolean;
}

/**
 * The Roll panel (issue #19): the dice, each modifier, the total and the degree against a DC typed
 * in before or after the roll; the second and third attack, the range increment, fortune and
 * misfortune and a SubstituteRoll as choices that recompute without rolling again; the Hero Point
 * reroll; the notes for the outcome; damage typed part by part with persistent damage apart. The
 * Roll is committed as one Play Action when the panel closes, so every amendment lands in the log.
 */
export function RollPanel(props: Props) {
  const { plan } = props;
  if (plan.kind === "unavailable") {
    return (
      <Modal title={plan.label} onClose={props.onClose} class="roll-panel">
        <p class="notice">{plan.reason}</p>
      </Modal>
    );
  }
  if (plan.kind === "check") return <CheckPanel {...props} plan={plan} />;
  if (plan.kind === "damage") return <DamagePanel {...props} plan={plan} />;
  return <InlinePanel {...props} plan={plan} />;
}

/* ------------------------------------ Checks ------------------------------------ */

type CheckPlan = Extract<RollPlan, { kind: "check" }>;

function neededDice(plan: CheckPlan, choices: CheckChoices): number {
  const probe = resolveCheck(plan.spec, choices, [1, 1], () => 1);
  return probe.substitution ? 0 : probe.keep ? 2 : 1;
}

function CheckPanel({ plan, physical, session, actor, links, busy, onCommit, onFollow, onClose, supply = cryptoDice }: Props & { plan: CheckPlan }) {
  const [draft, setDraft] = useState<CheckDraft>(() => ({ id: newRollId(), at: new Date().toISOString(), dice: [], choices: plan.choices, dc: plan.spec.dc, physical, rerollOf: null, heroPoint: false, consumed: false }));
  const [entry, setEntry] = useState<string[]>([]);
  const spec = useMemo(() => ({ ...plan.spec, dc: draft.dc }), [plan.spec, draft.dc]);
  const needed = neededDice(plan, draft.choices);
  const awaiting = draft.physical && draft.dice.length < needed;
  // A generated roll happens on first render; a physical one waits for the dice. Amendments reuse the dice.
  const result = useMemo(() => (awaiting ? null : resolveCheck(spec, draft.choices, draft.dice, supply, draft.physical)), [spec, draft.choices, draft.dice, draft.physical, awaiting, supply]);
  if (result && result.dice.length !== draft.dice.length) setDraft({ ...draft, dice: result.dice });

  const record = (r: CheckResult): RollRecord => ({ id: draft.id, at: draft.at, label: plan.label, origin: plan.origin, physical: draft.physical, rerollOf: draft.rerollOf, heroPoint: draft.heroPoint, kind: "check", spec, choices: draft.choices, result: r });
  const commit = (final: boolean) => {
    if (!result) return;
    const effects: TableAction[] = [];
    if (draft.heroPoint) effects.push({ kind: "spend-hero-point" });
    if (!draft.consumed && session) effects.push(...plan.consumes(result, session));
    if (final) effects.push(...plan.outcome(result));
    onCommit(record(result), effects);
  };
  const close = () => {
    if (result) commit(true);
    onClose();
  };
  const reroll = () => {
    if (!result) return;
    commit(false);
    setDraft({ id: newRollId(), at: new Date().toISOString(), dice: [], choices: { ...draft.choices, fortune: false, misfortune: false }, dc: draft.dc, physical: draft.physical, rerollOf: draft.id, heroPoint: true, consumed: true });
    setEntry([]);
  };
  const follow = (request: RollRequest) => {
    if (result) commit(true);
    onFollow(request);
  };
  const useEntered = () => {
    const values = entry.slice(0, needed).map((v) => Math.min(20, Math.max(1, Math.trunc(Number(v)) || 0)));
    if (values.length < needed || values.some((v) => v < 1)) return;
    setDraft({ ...draft, dice: [...draft.dice, ...values] });
    setEntry([]);
  };
  const setChoices = (changes: Partial<CheckChoices>) => setDraft({ ...draft, choices: { ...draft.choices, ...changes } });
  const heroPoints = session?.heroPoints ?? 0;
  const canReroll = !!session && heroPoints > 0 && !!result && isRerollable(result, draft.rerollOf);
  const substitution = result ? result.substitution : null;
  const optionalSubstitutions = spec.substitutions.filter((s) => !s.required);
  const degree = result?.degree ?? null;

  return (
    <Modal title={plan.label} onClose={close} class="roll-panel" header={<span class="roll-tags">{draft.heroPoint && <span class="tag">Hero Point reroll</span>}{draft.physical && <span class="tag">physical dice</span>}</span>}>
      {awaiting ? (
        <DiceEntry slots={[{ faces: 20, count: needed - draft.dice.length, label: needed === 2 ? "d20 (roll twice)" : "d20" }]} values={entry} onChange={setEntry} onUse={useEntered} onGenerate={() => setDraft({ ...draft, physical: false })} busy={busy} />
      ) : result ? (
        <>
          <CheckDice result={result} />

          <div class="roll-dc inline">
            <label>
              DC{" "}
              <input type="number" inputMode="numeric" min="0" aria-label="DC" placeholder={spec.dcLabel ?? "none"} value={draft.dc ?? ""} disabled={busy} onInput={(e) => { const raw = (e.currentTarget as HTMLInputElement).value.trim(); setDraft({ ...draft, dc: raw === "" ? null : Math.max(0, Math.trunc(Number(raw)) || 0) }); }} style="width:6rem" />
            </label>
            {degree ? (
              <DegreeBadge result={result} />
            ) : (
              <span class="muted">{spec.dcLabel ? `Against ${spec.dcLabel}: enter the DC for the degree.` : "Enter a DC for the degree of success."}</span>
            )}
          </div>

          {(
            <div class="roll-choices">
              {spec.map && (
                <span class="segmented" aria-label="Attack number">
                  {([0, 1, 2] as const).map((n) => (
                    <button type="button" key={n} class={draft.choices.map === n ? "active" : ""} disabled={busy} onClick={() => setChoices({ map: n })}>
                      {n === 0 ? "1st attack" : n === 1 ? `2nd (${spec.map!.penalties[0]})` : `3rd (${spec.map!.penalties[1]})`}
                    </button>
                  ))}
                </span>
              )}
              {spec.range && spec.range.increment && (
                <label class="inline">
                  Range{" "}
                  <select aria-label="Range increment" value={draft.choices.rangeIncrement ?? 1} disabled={busy} onChange={(e) => setChoices({ rangeIncrement: Number((e.currentTarget as HTMLSelectElement).value) })}>
                    {Array.from({ length: MAX_RANGE_INCREMENTS }, (_, i) => i + 1).map((n) => (
                      <option key={n} value={n}>{n === 1 ? `within ${spec.range!.increment} ft` : `${n}${n === 2 ? "nd" : n === 3 ? "rd" : "th"} increment, to ${spec.range!.increment! * n} ft (${rangePenalty(n)})`}</option>
                    ))}
                  </select>
                </label>
              )}
              {spec.range?.volley && (
                <label class="toggle"><input type="checkbox" checked={draft.choices.volley} disabled={busy} onChange={(e) => setChoices({ volley: (e.currentTarget as HTMLInputElement).checked })} /> within {spec.range.volley} ft (volley −2)</label>
              )}
              <label class="toggle" title="Roll twice, keep the higher"><input type="checkbox" checked={draft.choices.fortune} disabled={busy} onChange={(e) => setChoices({ fortune: (e.currentTarget as HTMLInputElement).checked })} /> Fortune</label>
              <label class="toggle" title="Roll twice, keep the lower"><input type="checkbox" checked={draft.choices.misfortune} disabled={busy} onChange={(e) => setChoices({ misfortune: (e.currentTarget as HTMLInputElement).checked })} /> Misfortune</label>
              {optionalSubstitutions.map((s) => (
                <label key={s.slug} class="toggle" title={s.sourceName ?? s.label}><input type="checkbox" checked={draft.choices.substitution === s.slug} disabled={busy} onChange={(e) => setChoices({ substitution: (e.currentTarget as HTMLInputElement).checked ? s.slug : null })} /> {s.label} ({s.value})</label>
              ))}
            </div>
          )}

          <CheckBreakdown spec={spec} result={result} />
          {result.notes.length > 0 && <Notes notes={result.notes} actor={actor} links={links} onFollow={follow} />}
          {spec.traits.length > 0 && <p class="roll-traits">{spec.traits.map((t) => <span key={t} class="tag trait">{t}</span>)}</p>}

          <div class="actions roll-actions">
            {plan.damage && (
              <>
                {(!degree || degree.value === "success" || degree.value === "criticalSuccess") && (
                  <button type="button" class="primary" disabled={busy} onClick={() => follow({ ...plan.damage!.request, asCritical: degree?.value === "criticalSuccess" && plan.damage!.hasCritical } as RollRequest)}>
                    {degree?.value === "criticalSuccess" && plan.damage.hasCritical ? "Roll critical damage" : "Roll damage"}
                  </button>
                )}
                {!degree && plan.damage.hasCritical && <button type="button" disabled={busy} onClick={() => follow({ ...plan.damage!.request, asCritical: true } as RollRequest)}>Roll critical damage</button>}
              </>
            )}
            {plan.follow && <button type="button" disabled={busy} onClick={() => follow(plan.follow!.request)}>{plan.follow.label}</button>}
            <button type="button" disabled={busy || !canReroll} title={!session ? "Open a Session to spend Hero Points" : heroPoints === 0 ? "No Hero Points left" : !result || isRerollable(result, draft.rerollOf) ? "Spend a Hero Point to reroll and keep the new result" : "A reroll or a fortune effect cannot be rerolled"} onClick={reroll}>
              Hero Point reroll{session ? ` (${heroPoints} left)` : ""}
            </button>
            <button type="button" disabled={busy} onClick={close}>Done</button>
          </div>
          {plan.ammunition && !draft.consumed && <p class="muted">Fires one {plan.ammunition.name} when closed.</p>}
        </>
      ) : null}
    </Modal>
  );
}

/* ------------------------------------ Damage ------------------------------------ */

type DamagePlan = Extract<RollPlan, { kind: "damage" }>;

function DamagePanel({ plan, physical, session, maxima = null, busy, onCommit, onFollow, onClose, supply = cryptoDice }: Props & { plan: DamagePlan }) {
  const valid = isValidFormula(plan.formula);
  const [state, setState] = useState<{ id: string; at: string; physical: boolean; result: DamageResult | null }>(() => ({ id: newRollId(), at: new Date().toISOString(), physical, result: physical || !valid ? null : rollDamage(plan.formula, { critical: plan.critical }, supply) }));
  const [entry, setEntry] = useState<string[]>([]);
  const [apply, setApply] = useState(false);
  const slots = useMemo(() => (valid ? diceOf(plan.formula) : []), [plan.formula, valid]);
  const result = state.result;
  const record = (r: DamageResult): RollRecord => ({ id: state.id, at: state.at, label: plan.label, origin: plan.origin, physical: state.physical, rerollOf: null, heroPoint: false, kind: "damage", formula: plan.formula, criticalFormula: plan.criticalFormula, critical: plan.critical, result: r });
  const effects = (r: DamageResult): TableAction[] => {
    if (!apply || !session) return [];
    if (plan.apply === "healing" && r.healing > 0) return [{ kind: "heal", amount: r.healing, source: plan.label }];
    if (plan.apply === "damage" && r.total > 0) return [{ kind: "damage", amount: r.total, source: plan.label, ...(plan.critical ? { critical: true } : {}), instances: damageInstancesOf(r), options: plan.options }];
    return [];
  };
  /** What the Session's IWR make of the damage (#22), for the apply label. */
  const iwr = result && plan.apply === "damage" && maxima ? applyIWR(maxima, damageInstancesOf(result), plan.options, plan.critical) : null;
  const close = () => {
    if (result) onCommit(record(result), effects(result));
    onClose();
  };
  const follow = (request: RollRequest) => {
    if (result) onCommit(record(result), effects(result));
    onFollow(request);
  };
  const useEntered = () => {
    const count = slots.reduce((n, s) => n + s.count, 0);
    const values = entry.slice(0, count).map((v) => Math.trunc(Number(v)) || 0);
    if (values.length < count || values.some((v) => v < 1)) return;
    setState({ ...state, result: rollDamage(plan.formula, { critical: plan.critical, physical: true }, fixedDice(values)) });
  };
  if (!valid) {
    return (
      <Modal title={plan.label} onClose={onClose} class="roll-panel">
        <p class="notice">This damage formula cannot be rolled: {plan.formula}</p>
      </Modal>
    );
  }
  const applyLabel = result ? (plan.apply === "healing" ? `Heal ${result.healing} HP` : iwr && iwr.applications.length > 0 ? `Take ${iwr.total} damage (${result.total} before ${iwr.applications.map(describeApplication).join(", ")})` : `Take ${result.total} damage`) : "";
  return (
    <Modal title={plan.label} onClose={close} class="roll-panel" header={<span class="roll-tags">{plan.critical && <span class="tag">critical</span>}{state.physical && <span class="tag">physical dice</span>}</span>}>
      {!result ? (
        <DiceEntry slots={slots} values={entry} onChange={setEntry} onUse={useEntered} onGenerate={() => setState({ ...state, physical: false, result: rollDamage(plan.formula, { critical: plan.critical }, supply) })} busy={busy} />
      ) : (
        <>
          <DamageResultView result={result} />
          {plan.breakdown.length > 0 && (
            <section class="breakdown" aria-label="Breakdown">
              <h3>Formula</h3>
              <ul class="plain breakdown-rows">
                {plan.breakdown.map((line, i) => <li key={i}><span class="bd-label">{line}</span></li>)}
              </ul>
            </section>
          )}
          {result.instances.some((i) => i.dice.length > 0) && (
            <section class="breakdown" aria-label="Dice">
              <h3>Dice</h3>
              <ul class="plain breakdown-rows">
                {result.instances.map((inst, i) => (
                  <li key={i}>
                    <span class="bd-label">{inst.persistent ? "persistent " : ""}{inst.type === "untyped" ? (inst.kind === "healing" ? "healing" : "damage") : inst.type}{inst.kind === "both" ? " (heals or harms)" : ""}</span>
                    <span class="bd-source muted">{inst.dice.map((d) => `${d.results.length}d${d.faces}: ${d.results.join(", ")}`).join(" · ") || "no dice"}</span>
                    <span class="bd-value">{inst.total}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
          <div class="actions roll-actions">
            {plan.apply && session && (
              <label class="toggle"><input type="checkbox" checked={apply} disabled={busy} onChange={(e) => setApply((e.currentTarget as HTMLInputElement).checked)} /> {applyLabel} (apply to self)</label>
            )}
            {plan.follow && <button type="button" disabled={busy} onClick={() => follow(plan.follow!.request)}>{plan.follow.label}</button>}
            <button type="button" disabled={busy} onClick={close}>Done</button>
          </div>
        </>
      )}
    </Modal>
  );
}

/** A damage result's instances as the Session takes them (#22): what lands now, with the precision and splash shares and materials. */
function damageInstancesOf(result: DamageResult) {
  return result.instances.filter((i) => !i.persistent && i.kind !== "healing").map((i) => ({ type: i.type, total: i.total, ...(i.precision > 0 ? { precision: i.precision } : {}), ...(i.splash > 0 ? { splash: i.splash } : {}), ...(i.materials.length > 0 ? { materials: [...i.materials] } : {}) }));
}

/** The typed totals of a damage result: what lands now, then persistent damage apart. */
export function DamageResultView({ result }: { result: DamageResult }) {
  const now = result.instances.filter((i) => !i.persistent);
  return (
    <div class="roll-damage">
      <div class="roll-dice">
        {now.map((inst, i) => (
          <span key={i} class={`die damage-${inst.type}`}>
            <span class="die-value">{inst.total}</span>
            <span class="die-label">{inst.type === "untyped" ? (inst.kind === "healing" ? "healing" : "untyped") : inst.type}{inst.kind === "healing" && inst.type !== "untyped" ? " healing" : inst.kind === "both" ? " (heals or harms)" : ""}</span>
            {(inst.precision > 0 || inst.splash > 0) && <span class="die-sub muted">{[inst.precision > 0 ? `${inst.precision} precision` : "", inst.splash > 0 ? `${inst.splash} splash` : ""].filter(Boolean).join(", ")}</span>}
          </span>
        ))}
        {now.length > 0 && (
          <span class="roll-sum">
            <span class="muted">=</span> <span class="roll-total">{result.healing > 0 && result.total === 0 ? result.healing : result.total}</span>
            {result.increasedFrom !== null && <span class="muted"> (raised from {result.increasedFrom})</span>}
          </span>
        )}
      </div>
      {result.persistent.length > 0 && (
        <p class="roll-persistent">
          {result.persistent.map((inst, i) => <span key={i} class="tag">{inst.total} persistent {inst.type}</span>)}
          <span class="muted"> · taken at the end of each turn; DC 15 flat check to end</span>
        </p>
      )}
    </div>
  );
}

/* ------------------------------------ Inline ------------------------------------ */

type InlinePlan = Extract<RollPlan, { kind: "inline" }>;

function InlinePanel({ plan, physical, busy, onCommit, onClose, supply = cryptoDice }: Props & { plan: InlinePlan }) {
  const valid = isValidFormula(plan.formula);
  const [state, setState] = useState<{ id: string; at: string; physical: boolean; result: RolledFormula | null }>(() => ({ id: newRollId(), at: new Date().toISOString(), physical, result: physical || !valid ? null : rollFormula(plan.formula, supply) }));
  const [entry, setEntry] = useState<string[]>([]);
  const slots = useMemo(() => (valid ? diceOf(plan.formula) : []), [plan.formula, valid]);
  const result = state.result;
  const close = () => {
    if (result) onCommit({ id: state.id, at: state.at, label: plan.label, origin: plan.origin, physical: state.physical, rerollOf: null, heroPoint: false, kind: "inline", formula: plan.formula, flavor: plan.flavor, result }, []);
    onClose();
  };
  const useEntered = () => {
    const count = slots.reduce((n, s) => n + s.count, 0);
    const values = entry.slice(0, count).map((v) => Math.trunc(Number(v)) || 0);
    if (values.length < count || values.some((v) => v < 1)) return;
    setState({ ...state, result: rollFormula(plan.formula, fixedDice(values)) });
  };
  if (!valid) {
    return (
      <Modal title={plan.label} onClose={onClose} class="roll-panel">
        <p class="notice">This formula cannot be rolled: {plan.formula}</p>
      </Modal>
    );
  }
  return (
    <Modal title={plan.flavor ?? plan.label} onClose={close} class="roll-panel" header={<span class="roll-tags">{state.physical && <span class="tag">physical dice</span>}</span>}>
      {!result ? (
        <DiceEntry slots={slots} values={entry} onChange={setEntry} onUse={useEntered} onGenerate={() => setState({ ...state, physical: false, result: rollFormula(plan.formula, supply) })} busy={busy} />
      ) : (
        <>
          <div class="roll-dice">
            {result.dice.map((d, i) => (
              <span key={i} class="die kept">
                <span class="die-value">{d.kept.reduce((s, r) => s + r, 0)}</span>
                <span class="die-label">{d.results.length}d{d.faces}{d.results.length > 1 ? `: ${d.results.join(", ")}` : ""}</span>
              </span>
            ))}
            <span class="roll-sum"><span class="muted">{plan.formula} =</span> <span class="roll-total">{result.total}</span></span>
          </div>
          <div class="actions roll-actions">
            <button type="button" disabled={busy} onClick={close}>Done</button>
          </div>
        </>
      )}
    </Modal>
  );
}

/* ------------------------------------ Result views ------------------------------------ */

/** The d20 (or both, with the kept one marked, or the substituted value), the modifier and the total. Shared with the Roll Log (#20). */
export function CheckDice({ result }: { result: CheckResult }) {
  return (
    <div class="roll-dice" aria-label="Dice">
      {result.substitution ? (
        <span class="die kept substituted" title={result.substitution.label}><span class="die-value">{result.substitution.value}</span><span class="die-label">{result.substitution.label}</span></span>
      ) : (
        (result.keep ? result.dice : [result.die]).map((d, i) => {
          const keptIndex = !result.keep ? 0 : result.dice.indexOf(result.keep === "higher" ? Math.max(...result.dice) : Math.min(...result.dice));
          const kept = i === keptIndex;
          return (
            <span key={i} class={`die${kept ? " kept" : " dropped"}${d === 20 ? " nat20" : d === 1 ? " nat1" : ""}`}>
              <span class="die-value">{d}</span>
              <span class="die-label">d20</span>
            </span>
          );
        })
      )}
      <span class="roll-sum">
        <span class="muted">{signed(result.modifier)} =</span> <span class="roll-total">{result.total}</span>
      </span>
      {result.keep && <span class="tag">{result.keep === "higher" ? "fortune" : "misfortune"}: {(result.keep === "higher" ? result.fortune : result.misfortune).join(", ")}</span>}
      {result.cancelled && <span class="tag">fortune and misfortune cancel</span>}
      {result.natural === 20 && <span class="tag ok">natural 20</span>}
      {result.natural === 1 && <span class="tag warn">natural 1</span>}
    </div>
  );
}

/** The degree of success against the DC, with an adjustment named when one applied. */
export function DegreeBadge({ result }: { result: CheckResult }) {
  const degree = result.degree;
  if (!degree) return null;
  return (
    <span class={`degree degree-${degree.value}`} role="status">
      {DEGREE_LABELS[degree.value]}
      {degree.adjustment && <span class="muted"> ({DEGREE_LABELS[degree.unadjusted].toLowerCase()} → {degree.adjustment.label})</span>}
      <span class="muted"> · {signed(result.total - degree.dc)}</span>
    </span>
  );
}

/** Every modifier line of a check with its type and source, the die first and the total last. */
export function CheckBreakdown({ spec, result }: { spec: Pick<CheckSpec, "override">; result: CheckResult }) {
  return (
    <section class="breakdown" aria-label="Breakdown">
      <h3>Breakdown</h3>
      <ul class="plain breakdown-rows">
        <li class="base"><span class="bd-label">{result.substitution ? `${result.substitution.label} in place of the d20` : result.dice.length === 2 ? `d20 twice: ${result.dice.join(", ")}, keep ${result.keep === "higher" ? "higher" : "lower"}` : "d20"}</span><span class="bd-value">{result.die}</span></li>
        {spec.override !== null && <li><span class="bd-label">Override<span class="tag">override</span></span><span class="bd-value">{signed(spec.override)}</span></li>}
        {result.lines.filter((l) => l.enabled || !spec.override).map((l, i) => (
          <li key={i} class={l.enabled ? "" : "off"}>
            <span class="bd-label">{l.label}<span class="tag">{l.type}</span>{!l.enabled && <span class="tag">off</span>}{l.removeAfterRoll && l.enabled && <span class="tag">ends after this roll</span>}</span>
            <span class="bd-source muted">{l.sourceName ?? ""}</span>
            <span class="bd-value">{signed(l.value)}</span>
          </li>
        ))}
        <li class="total"><span class="bd-label">Total</span><span class="bd-value">{result.total}</span></li>
      </ul>
    </section>
  );
}

/* ------------------------------------ Pieces ------------------------------------ */

/** Inputs for the physical dice a Roll needs, one per die, in the formula's order. */
function DiceEntry({ slots, values, onChange, onUse, onGenerate, busy }: { slots: DieSlot[]; values: string[]; onChange: (v: string[]) => void; onUse: () => void; onGenerate: () => void; busy: boolean }) {
  const inputs = slots.flatMap((s) => Array.from({ length: s.count }, (_, i) => ({ faces: s.faces, label: s.label, nth: s.count > 1 ? i + 1 : null })));
  const complete = inputs.every((_, i) => { const n = Math.trunc(Number(values[i])); return n >= 1 && n <= inputs[i]!.faces; });
  return (
    <div class="dice-entry">
      <p class="muted">{inputs.length === 0 ? "No dice to enter: the formula is a constant." : "Enter what the physical dice show."}</p>
      <div class="dice-entry-grid">
        {inputs.map((d, i) => (
          <label key={i}>
            <span class="muted">d{d.faces}{d.label ? ` ${d.label}` : ""}{d.nth ? ` #${d.nth}` : ""}</span>
            <input type="number" inputMode="numeric" min="1" max={d.faces} aria-label={`d${d.faces}${d.nth ? ` #${d.nth}` : ""}`} value={values[i] ?? ""} disabled={busy} onInput={(e) => { const next = [...values]; next[i] = (e.currentTarget as HTMLInputElement).value; onChange(next); }} />
          </label>
        ))}
      </div>
      <div class="actions">
        <button type="button" class="primary" disabled={busy || !complete} onClick={onUse}>Use these dice</button>
        <button type="button" disabled={busy} onClick={onGenerate}>Roll for me instead</button>
      </div>
    </div>
  );
}

/** Notes for the outcome, with their inline Roll buttons and links live. */
export function Notes({ notes, actor, links, onFollow }: { notes: { title: string | null; text: string; sourceName: string | null }[]; actor: ActorContext | null; links: LinkResolver | null; onFollow: (request: RollRequest) => void }) {
  const onClick = (e: MouseEvent) => {
    const hit = readEnrichedClick(e.target);
    if (!hit || hit.kind !== "roll") return;
    e.preventDefault();
    onFollow(hit.request);
  };
  return (
    <section class="roll-notes" aria-label="Notes">
      <h3>Notes</h3>
      <ul class="plain">
        {notes.map((n, i) => (
          <li key={i} class="desc" onClick={onClick}>
            {n.title ? <strong>{n.title}: </strong> : null}
            <span dangerouslySetInnerHTML={{ __html: enrichDescription(n.text, { actor, links }) }} />
            {n.sourceName ? <span class="muted"> · {n.sourceName}</span> : null}
          </li>
        ))}
      </ul>
    </section>
  );
}
